const IMAGE_SELECTOR = 'img[data-strict-lazy-image]';
const OBSERVER_MARGIN = '160px';
const OBSERVER_MARGIN_PX = 160;
const HYDRATE_EVENT = 'theme:hydrate-lazy-image';

const observedImages = new WeakSet();
const pendingContainers = new Set();

let visibilityFrame = null;

function isSuppressed(image) {
  const slide = image.closest('slideshow-slide');
  if (slide && slide.parentElement?.firstElementChild === slide) {
    const parentSuppressed = slide.parentElement?.closest('[aria-hidden="true"], [inert], [hidden]');
    return Boolean(parentSuppressed);
  }

  return Boolean(
    image.closest('[aria-hidden="true"], [inert], [hidden]')
  );
}

function isNearViewport(image) {
  if (isSuppressed(image)) return false;

  const bounds = image.getBoundingClientRect();

  return (
    bounds.bottom >= -OBSERVER_MARGIN_PX &&
    bounds.right >= -OBSERVER_MARGIN_PX &&
    bounds.top <= window.innerHeight + OBSERVER_MARGIN_PX &&
    bounds.left <= window.innerWidth + OBSERVER_MARGIN_PX
  );
}

function hydrateImage(image) {
  if (!image.matches(IMAGE_SELECTOR)) return;

  const sourceSet = image.dataset.srcset;
  const source = image.dataset.src;

  // Remove the marker before changing src/srcset.
  image.removeAttribute('data-strict-lazy-image');

  if (sourceSet) {
    image.srcset = sourceSet;
  }

  if (source) {
    image.src = source;
  }

  delete image.dataset.srcset;
  delete image.dataset.src;
}

const imageObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const image = entry.target;

            if (!(image instanceof HTMLImageElement)) {
              continue;
            }

            if (isSuppressed(image)) {
              continue;
            }

            observer.unobserve(image);
            hydrateImage(image);
          }
        },
        {
          rootMargin: OBSERVER_MARGIN,
          threshold: 0.01,
        }
      )
    : null;

function observeImage(image) {
  if (!image.matches(IMAGE_SELECTOR)) return;

  if (observedImages.has(image)) return;

  observedImages.add(image);

  if (imageObserver) {
    imageObserver.observe(image);
  } else if (isNearViewport(image)) {
    hydrateImage(image);
  }
}

function observeImages(root) {
  if (root instanceof HTMLImageElement) {
    observeImage(root);
  }

  if (!root.querySelectorAll) return;

  const images = root.querySelectorAll(IMAGE_SELECTOR);

  for (const image of images) {
    observeImage(image);
  }
}

function hydrateEligibleImages(root) {
  if (root instanceof HTMLImageElement) {
    if (
      root.matches(IMAGE_SELECTOR) &&
      isNearViewport(root)
    ) {
      imageObserver?.unobserve(root);
      hydrateImage(root);
    }
  }

  if (!root.querySelectorAll) return;

  const images = root.querySelectorAll(IMAGE_SELECTOR);

  for (const image of images) {
    if (!isNearViewport(image)) continue;

    imageObserver?.unobserve(image);
    hydrateImage(image);
  }
}

// Batch visibility checks into a single animation frame.
function scheduleVisibilityCheck(container) {
  pendingContainers.add(container);

  if (visibilityFrame !== null) return;

  visibilityFrame = requestAnimationFrame(() => {
    visibilityFrame = null;

    const containers = [...pendingContainers];
    pendingContainers.clear();

    for (const container of containers) {
      // Skip nested containers already covered by a parent.
      const covered = containers.some(
        (parent) =>
          parent !== container &&
          parent.contains(container)
      );

      if (covered) continue;

      hydrateEligibleImages(container);
    }
  });
}

function initializeStrictLazyMedia() {
  observeImages(document);

  const mutationObserver = new MutationObserver(
    (mutations) => {
      for (const mutation of mutations) {

        if (mutation.type === 'attributes') {
          const target = mutation.target;

          if (target instanceof Element) {
            scheduleVisibilityCheck(target);
          }

          continue;
        }

        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          observeImages(node);
        }
      }
    }
  );

  mutationObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [
      'aria-hidden',
      'hidden',
      'inert',
      'open',
      'data-inline-open'
    ],
    childList: true,
    subtree: true,
  });

  document.addEventListener('dialog:open', (event) => {
    const target = event.target;
    if (target instanceof Element) {
      scheduleVisibilityCheck(target);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeStrictLazyMedia,
    { once: true }
  );
} else {
  initializeStrictLazyMedia();
}

document.addEventListener(
  'shopify:section:load',
  (event) => {
    const target = event.target;

    if (target instanceof Element) {
      observeImages(target);
    }
  }
);

document.addEventListener(
  HYDRATE_EVENT,
  (event) => {
    const image = event.target;

    if (
      !(image instanceof HTMLImageElement) ||
      !image.matches(IMAGE_SELECTOR)
    ) {
      return;
    }

    imageObserver?.unobserve(image);
    hydrateImage(image);
  }
);